(async () => {
  const WORKER = "https://mp-geo-detect.harvey-1c7.workers.dev";
  const COUNTRY = "AU";
  // Warehouse is in Melbourne, so all schedule/cutoff times are interpreted in
  // Melbourne time (adjusts for daylight saving automatically).
  const TIMEZONE = "Australia/Melbourne";

  // Geo schedule + cutoff config, injected via window.themeVariables.
  const geoLocate = window.themeVariables?.settings?.geoLocate ?? {};

  const MINUTES_PER_DAY = 1440;
  const TIME_RE = /^\d{1,2}:\d{2}$/; // 24h time, e.g. "9:00", "09:00" or "00:01"

  const DAY_INDEX = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 };

  // Resolve a day name to its index (0-6), or undefined if unrecognised.
  const dayToIndex = (day) => {
    if (typeof day !== "string") return undefined;
    return DAY_INDEX[day.trim().toLowerCase().slice(0, 3)];
  };

  const isValidTime = (time) => {
    if (typeof time !== "string" || !TIME_RE.test(time)) return false;
    const [hour, minute] = time.split(":").map(Number);
    return hour <= 23 && minute <= 59;
  };

  const timeToMinutes = (time) => {
    const [hour, minute] = time.split(":").map(Number);
    return hour * 60 + minute;
  };

  const toMinuteOfWeek = (dayIndex, time) => dayIndex * MINUTES_PER_DAY + timeToMinutes(time);

  // Current minute-of-week in the given timezone, or null if it can't be
  // resolved (bad timezone / unexpected parts).
  const currentMinuteOfWeek = (timezone, context) => {
    let parts;
    try {
      parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-AU", {
          timeZone: timezone,
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(new Date())
          .map(({ type, value }) => [type, value]),
      );
    } catch (error) {
      console.error(`[geo-locate] failed to resolve time in "${timezone}" (${context}); hiding geo message.`, error);
      return null;
    }

    const dayIndex = dayToIndex(parts.weekday);
    const hour = Number(parts.hour);
    const minute = Number(parts.minute);
    if (dayIndex === undefined || Number.isNaN(hour) || Number.isNaN(minute)) {
      console.error(`[geo-locate] could not derive current time in "${timezone}" (${context}); hiding geo message.`);
      return null;
    }

    return dayIndex * MINUTES_PER_DAY + hour * 60 + minute;
  };

  // Handles windows that wrap across the end of the week (e.g. Sat -> Tue).
  const isWithinWindow = (now, start, end) =>
    start > end ? now >= start || now < end : now >= start && now < end;

  // Returns null when the schedule is structurally valid, otherwise a
  // descriptive reason string for logging.
  const scheduleSettingsError = (schedule) => {
    if (dayToIndex(schedule.startDay) === undefined) return `invalid startDay: ${JSON.stringify(schedule.startDay)}`;
    if (dayToIndex(schedule.endDay) === undefined) return `invalid endDay: ${JSON.stringify(schedule.endDay)}`;
    if (!isValidTime(schedule.startTime)) return `invalid startTime: ${JSON.stringify(schedule.startTime)}`;
    if (!isValidTime(schedule.endTime)) return `invalid endTime: ${JSON.stringify(schedule.endTime)}`;
    return null;
  };

  // Whether the geo message is allowed to show right now, per the weekly window.
  // Fail closed because it is better to withhold the same-day-delivery promise
  // than to make it when the schedule gate itself is broken.
  const withinSchedule = () => {
    if (!geoLocate.enabled) return true; // If schedule isn't enabled - skip the time checks.

    const settingsError = scheduleSettingsError(geoLocate);
    if (settingsError) {
      console.error(`[geo-locate] geo schedule enabled but ${settingsError}.`);
      return false;
    }

    const now = currentMinuteOfWeek(TIMEZONE, "schedule");
    if (now === null) return false;

    const start = toMinuteOfWeek(dayToIndex(geoLocate.startDay), geoLocate.startTime);
    const end = toMinuteOfWeek(dayToIndex(geoLocate.endDay), geoLocate.endTime);
    return isWithinWindow(now, start, end);
  };

  // Whether the daily same-day cutoff has passed, so the message should be
  // hidden (same-day delivery is no longer possible today).
  // Evaluated in the schedule timezone and applies whether or not the weekly
  // schedule is enabled.
  const pastCutoff = () => {
    const cutoffTime = geoLocate.cutoffTime;
    if (!cutoffTime || cutoffTime.trim() === "") return false;

    if (!isValidTime(cutoffTime)) {
      console.error(`[geo-locate] invalid cutoffTime: ${JSON.stringify(cutoffTime)}`);
      return true;
    }

    const now = currentMinuteOfWeek(TIMEZONE, "cutoff");
    if (now === null) return true;

    return now % MINUTES_PER_DAY > timeToMinutes(cutoffTime);
  };

  // Klaviyo stores __kla_id as URL-encoded. This is only present if the user
  // has a Klaviyo profile. If not this does not set the $exchange_id field
  const getExchangeId = () => {
    const cookie = document.cookie.match(/__kla_id=([^;]+)/)?.[1];
    if (!cookie) return null;
    try {
      return JSON.parse(atob(decodeURIComponent(cookie))).$exchange_id || null;
    } catch {
      return null;
    }
  };

  // Ask the worker whether the visitor is within range of this element's geo
  // target, and reveal the element if so.
  const revealIfWithinRange = async (el, kx) => {
    const params = new URLSearchParams({
      kx,
      country: COUNTRY,
      address: el.dataset.geoAddress,
      distances: el.dataset.geoDistance,
      region: el.dataset.geoRegion,
    });
    try {
      const res = await fetch(`${WORKER}/?${params}`);
      if (!res.ok) throw new Error(`Worker responded with HTTP ${res.status}`);
      const { within } = await res.json();
      if (within) el.hidden = false;
    } catch (error) {
      console.warn("[geo-locate] geo detection request failed; message left hidden.", error);
    }
  };

  if (!withinSchedule()) return;
  if (pastCutoff()) return;

  const kx = getExchangeId();
  if (!kx) return;

  const elements = [...document.querySelectorAll("[data-geo-address][data-geo-distance][data-geo-region]")];
  if (!elements.length) return;

  await Promise.all(elements.map((el) => revealIfWithinRange(el, kx)));
})();
